/**
 * Device Capabilities Detection
 * Detects device features to provide context to AI
 */

// Cache capabilities to avoid re-detection
let cachedCapabilities = null

/**
 * Detect all device capabilities
 * @returns {Promise<Object>} Device capabilities object
 */
export async function detectDeviceCapabilities() {
  if (cachedCapabilities) return cachedCapabilities

  const capabilities = {
    // Device type
    deviceType: detectDeviceType(),
    
    // Input methods
    input: {
      touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      mouse: window.matchMedia('(pointer: fine)').matches,
      keyboard: !isMobileDevice(), // Estimate - mobile devices typically don't have physical keyboards
      stylus: window.matchMedia('(pointer: fine) and (hover: none)').matches
    },
    
    // Screen info
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      pixelRatio: window.devicePixelRatio || 1,
      orientation: getOrientation(),
      colorDepth: window.screen.colorDepth
    },
    
    // Sensors
    sensors: await detectSensors(),
    
    // Media devices
    media: await detectMediaDevices(),
    
    // Browser capabilities
    browser: {
      webGL: detectWebGL(),
      webGL2: detectWebGL2(),
      webAudio: 'AudioContext' in window || 'webkitAudioContext' in window,
      webRTC: 'RTCPeerConnection' in window,
      serviceWorker: 'serviceWorker' in navigator,
      notifications: 'Notification' in window,
      geolocation: 'geolocation' in navigator,
      vibration: 'vibrate' in navigator,
      battery: 'getBattery' in navigator,
      speechRecognition: 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window,
      speechSynthesis: 'speechSynthesis' in window
    },
    
    // Connection info
    connection: getConnectionInfo(),
    
    // Platform
    platform: {
      os: detectOS(),
      browser: detectBrowser(),
      standalone: window.matchMedia('(display-mode: standalone)').matches
    }
  }

  cachedCapabilities = capabilities
  return capabilities
}

/**
 * Get a concise summary string for AI context
 */
export async function getDeviceContextForAI() {
  const caps = await detectDeviceCapabilities()
  
  const parts = []
  
  // Device type
  parts.push(`Device: ${caps.deviceType}`)
  
  // Input methods
  const inputs = []
  if (caps.input.touch) inputs.push('touch screen')
  if (caps.input.mouse) inputs.push('mouse')
  if (caps.input.keyboard) inputs.push('keyboard')
  if (caps.input.stylus) inputs.push('stylus')
  if (inputs.length) parts.push(`Input: ${inputs.join(', ')}`)
  
  // Screen
  parts.push(`Screen: ${caps.screen.width}×${caps.screen.height}px, ${caps.screen.orientation}`)
  
  // Sensors
  const sensors = []
  if (caps.sensors.accelerometer) sensors.push('accelerometer')
  if (caps.sensors.gyroscope) sensors.push('gyroscope')
  if (caps.sensors.magnetometer) sensors.push('compass')
  if (caps.sensors.ambientLight) sensors.push('ambient light')
  if (caps.sensors.proximity) sensors.push('proximity')
  if (sensors.length) {
    parts.push(`Sensors: ${sensors.join(', ')}`)
  } else {
    parts.push('Sensors: none detected')
  }
  
  // Media
  const mediaInfo = []
  if (caps.media.cameras > 0) {
    mediaInfo.push(`${caps.media.cameras} camera${caps.media.cameras > 1 ? 's' : ''}`)
  }
  if (caps.media.microphones > 0) {
    mediaInfo.push(`${caps.media.microphones} microphone${caps.media.microphones > 1 ? 's' : ''}`)
  }
  if (mediaInfo.length) {
    parts.push(`Media: ${mediaInfo.join(', ')}`)
  }
  
  // Key capabilities
  const features = []
  if (caps.browser.webGL2) features.push('WebGL2')
  else if (caps.browser.webGL) features.push('WebGL')
  if (caps.browser.webAudio) features.push('Web Audio')
  if (caps.browser.vibration) features.push('haptics')
  if (features.length) {
    parts.push(`Features: ${features.join(', ')}`)
  }
  
  return `[${parts.join(' | ')}]`
}

/**
 * Detect device type
 */
function detectDeviceType() {
  const ua = navigator.userAgent.toLowerCase()
  const width = window.screen.width
  
  if (/ipad|tablet|playbook|silk/i.test(ua) || (width >= 768 && width <= 1024)) {
    return 'tablet'
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile phone'
  }
  return 'desktop/laptop'
}

/**
 * Check if mobile device
 */
function isMobileDevice() {
  return /mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(navigator.userAgent.toLowerCase())
}

/**
 * Get screen orientation
 */
function getOrientation() {
  if (window.screen.orientation) {
    return window.screen.orientation.type.includes('portrait') ? 'portrait' : 'landscape'
  }
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
}

/**
 * Detect available sensors
 */
async function detectSensors() {
  const sensors = {
    accelerometer: false,
    gyroscope: false,
    magnetometer: false,
    ambientLight: false,
    proximity: false
  }

  // Check Sensor API (Chrome/Edge)
  if ('Accelerometer' in window) {
    try {
      const result = await navigator.permissions.query({ name: 'accelerometer' })
      sensors.accelerometer = result.state !== 'denied'
    } catch {
      sensors.accelerometer = true // Assume available if can't check
    }
  }
  
  // Fallback: DeviceMotionEvent (Safari/Firefox)
  if (!sensors.accelerometer && 'DeviceMotionEvent' in window) {
    sensors.accelerometer = true
    sensors.gyroscope = true
  }
  
  if ('Gyroscope' in window) {
    sensors.gyroscope = true
  }
  
  if ('DeviceOrientationEvent' in window) {
    sensors.gyroscope = true
    sensors.magnetometer = true // Compass available via orientation
  }
  
  if ('AmbientLightSensor' in window) {
    sensors.ambientLight = true
  }
  
  // Proximity - limited support
  if ('ProximitySensor' in window) {
    sensors.proximity = true
  }

  return sensors
}

/**
 * Detect media devices (cameras, microphones)
 */
async function detectMediaDevices() {
  const result = {
    cameras: 0,
    microphones: 0,
    speakers: 0,
    cameraLabels: []
  }

  if (!navigator.mediaDevices?.enumerateDevices) {
    return result
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    
    for (const device of devices) {
      if (device.kind === 'videoinput') {
        result.cameras++
        if (device.label) {
          result.cameraLabels.push(device.label.toLowerCase().includes('front') ? 'front' : 'back')
        }
      } else if (device.kind === 'audioinput') {
        result.microphones++
      } else if (device.kind === 'audiooutput') {
        result.speakers++
      }
    }
  } catch (e) {
    // Permission denied or not available
  }

  return result
}

/**
 * Detect WebGL support
 */
function detectWebGL() {
  try {
    const canvas = document.createElement('canvas')
    return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
  } catch {
    return false
  }
}

/**
 * Detect WebGL2 support
 */
function detectWebGL2() {
  try {
    const canvas = document.createElement('canvas')
    return !!canvas.getContext('webgl2')
  } catch {
    return false
  }
}

/**
 * Get connection info
 */
function getConnectionInfo() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
  if (!conn) return { type: 'unknown' }
  
  return {
    type: conn.effectiveType || conn.type || 'unknown',
    downlink: conn.downlink,
    saveData: conn.saveData
  }
}

/**
 * Detect OS
 */
function detectOS() {
  const ua = navigator.userAgent
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Mac OS X/i.test(ua)) return 'macOS'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
  if (/Android/i.test(ua)) return 'Android'
  if (/Linux/i.test(ua)) return 'Linux'
  if (/CrOS/i.test(ua)) return 'Chrome OS'
  return 'Unknown'
}

/**
 * Detect browser
 */
function detectBrowser() {
  const ua = navigator.userAgent
  if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) return 'Chrome'
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari'
  if (/Firefox/i.test(ua)) return 'Firefox'
  if (/Edge|Edg/i.test(ua)) return 'Edge'
  if (/Opera|OPR/i.test(ua)) return 'Opera'
  return 'Unknown'
}

/**
 * Clear cached capabilities (useful if orientation changes, etc.)
 */
export function clearCapabilitiesCache() {
  cachedCapabilities = null
}

// Listen for orientation changes
if (typeof window !== 'undefined') {
  window.addEventListener('orientationchange', clearCapabilitiesCache)
  window.addEventListener('resize', () => {
    // Debounced cache clear on resize
    clearTimeout(window._capsCacheTimeout)
    window._capsCacheTimeout = setTimeout(clearCapabilitiesCache, 500)
  })
}
